import { useAppSelector } from '@hooks/useRedux';
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from '@hooks/menu';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import { selectCanManageMenu } from '@store/auth/authSelectors';
import { Edit2, Package, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import type { MenuCategory } from '../../../types/menu';
import { ReadOnlyAccessNotice } from '../../common';
import { DeleteConfirmModal } from '../departments/DeleteConfirmModal';
import { CategoryFormModal } from './CategoryFormModal';

export const Categories: React.FC = () => {
  const outletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);
  const canManageMenu = useAppSelector(selectCanManageMenu);

  const {
    data: allCategories = [],
    isLoading,
    isError,
    refetch,
  } = useCategories(outletId);
  const categories = allCategories.filter((c) => c.status === 'active');
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const isSubmitting =
    createCategory.isPending ||
    updateCategory.isPending ||
    deleteCategory.isPending;

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MenuCategory | null>(
    null,
  );

  const handleCreateCategory = (data: {
    name: string;
    description?: string;
  }) => {
    if (!outletId || !canManageMenu) return;
    createCategory.mutate(
      { ...data, outlet_id: outletId },
      {
        onSuccess: () => {
          setIsCategoryModalOpen(false);
          setSelectedCategory(null);
        },
      },
    );
  };

  const handleUpdateCategory = (data: {
    name: string;
    description?: string;
  }) => {
    if (!selectedCategory || !canManageMenu) return;
    updateCategory.mutate(
      { id: selectedCategory.id, ...data },
      {
        onSuccess: () => {
          setIsCategoryModalOpen(false);
          setSelectedCategory(null);
        },
      },
    );
  };

  const handleDeleteConfirm = () => {
    if (selectedCategory && canManageMenu) {
      deleteCategory.mutate(selectedCategory.id, {
        onSuccess: () => setSelectedCategory(null),
      });
    }
    setIsDeleteModalOpen(false);
  };

  const openCreateCategory = () => {
    if (!canManageMenu) return;
    setSelectedCategory(null);
    setIsCategoryModalOpen(true);
  };

  const openEditCategory = (category: MenuCategory) => {
    if (!canManageMenu) return;
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
  };

  const openDeleteCategory = (category: MenuCategory) => {
    if (!canManageMenu) return;
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {!canManageMenu ? (
        <ReadOnlyAccessNotice message="You can view categories here, but only authorized roles can create, update, or delete them." />
      ) : null}

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Categories</h3>
        {canManageMenu ? (
          <button
            type="button"
            onClick={openCreateCategory}
            disabled={!outletId}
            className="flex items-center gap-2 px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors text-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} />
            <span>Add Category</span>
          </button>
        ) : null}
      </div>

      {isLoading && categories.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pos-primary" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <span className="text-red-500 font-bold text-xl">!</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Failed to load categories
          </h3>
          <p className="text-gray-500 mb-6 text-center max-w-sm">
            There was a problem loading the category data. Please try again.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors"
          >
            Retry Loading
          </button>
        </div>
      ) : categories.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Package size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No categories yet
          </h3>
          <p className="text-gray-500 mb-6">
            {canManageMenu
              ? 'Create your first category to get started.'
              : 'Categories created for this outlet will appear here once an authorized user adds them.'}
          </p>
          {canManageMenu ? (
            <button
              type="button"
              onClick={openCreateCategory}
              disabled={!outletId}
              className="inline-flex items-center gap-2 px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={20} />
              <span>Add Category</span>
            </button>
          ) : null}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-500">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Outlet</div>
            <div className="col-span-4">&nbsp;</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-gray-100">
            {categories.map((category) => (
              <div
                key={category.id}
                className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-gray-50 transition-colors"
              >
                <div className="col-span-3 font-medium text-gray-900 text-sm truncate">
                  {category.name}
                </div>
                <div className="col-span-3 text-sm text-gray-600 truncate">
                  {category.outlet_name || selectedOutlet?.name || '—'}
                </div>
                <div className="col-span-4 text-gray-500 truncate">
                  {category.description || ''}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  {canManageMenu ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openEditCategory(category)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openDeleteCategory(category)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CategoryFormModal
        isOpen={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
          setSelectedCategory(null);
        }}
        onSubmit={
          selectedCategory ? handleUpdateCategory : handleCreateCategory
        }
        category={selectedCategory}
        isLoading={isSubmitting}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedCategory(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Category"
        message={`Are you sure you want to delete "${selectedCategory?.name}"? Items in this category will become uncategorized.`}
        isLoading={isSubmitting}
      />
    </div>
  );
};
